import { apiFetch } from "./client";

export type Booking = {
  id: number;
  buchungsreferenz: string;
  gastname: string;
  gruppenname: string;
  anrede: string;
  vorname: string;
  nachname: string;
  email: string;
  einheiten_ids: number[];
  einheiten_namen: string[];
  gast_typ: string | null;
  check_in: string;
  check_out: string;
  check_in_time: string | null;
  check_out_time: string | null;
  gaesteanzahl: number;
  personen_ab_16: number | null;
  kinder_6_bis_15: number | null;
  kinder_unter_6: number | null;
  nachreinigung_eur: number | null;
  status: string;
  bemerkung: string | null;
  telefon: string | null;
  gruppenart: string | null;
  adresse_strasse: string | null;
  adresse_plz: string | null;
  adresse_ort: string | null;
  adresse_land: string | null;
  buchungsdatum: string | null;
  saison: string | null;
  rechnung_bemerkung: string | null;
  rechnung_zahlungsziel: string | null;
  rechnung_fehlteile: string | null;
  kurtaxe_betrag: number | null;
  anzahlung_betrag: number | null;
  anzahlung_erhalten: boolean | null;
  anzahlung_erhalten_am: string | null;
  schluesselcode_eg: string | null;
  schluesselcode_og: string | null;
  schluesselcode_dg: string | null;
  schluesselcode_lz: string | null;
  storniert_am: string | null;
  bestaetigung_extern_versandt: boolean;
  bestaetigung_extern_versandt_am: string | null;
  infos_gruppe_extern_versandt: boolean;
  infos_gruppe_extern_versandt_am: string | null;
  rechnung_versandt_am: string | null;
  bezahlt_am: string | null;
  hat_konflikt: boolean;
  gruppenleiter_anrede: string | null;
  gruppenleiter_geburtsdatum: string | null;
  gruppenleiter_vorname: string | null;
  gruppenleiter_nachname: string | null;
  gruppenleiter_adresse_strasse: string | null;
  gruppenleiter_adresse_hausnummer: string | null;
  gruppenleiter_adresse_plz: string | null;
  gruppenleiter_adresse_ort: string | null;
  gruppenleiter_adresse_land: string | null;
  meldescheinnummer: string | null;
  konus_link_versandt_am: string | null;
  konus_link_versandt: boolean;
  konus_daten_eingetragen: boolean;
  konus_daten_eingetragen_am: string | null;
  rechnung_rabatt_prozent: number | null;
  rechnung_rabatt_grund: string | null;
  rechnungsbetrag_gesamt: number | null;
};

export type BookingPatch = Partial<{
  gruppenname: string;
  anrede: string;
  vorname: string;
  nachname: string;
  gastname: string;
  email: string;
  einheiten_ids: number[];
  check_in: string;
  check_out: string;
  check_in_time: string;
  check_out_time: string;
  personen_ab_16: number;
  kinder_6_bis_15: number;
  kinder_unter_6: number;
  nachreinigung_eur: number;
  status: string;
  bemerkung: string;
  telefon: string;
  gruppenart: string;
  saison: string;
  adresse_strasse: string;
  adresse_plz: string;
  adresse_ort: string;
  adresse_land: string;
  buchungsdatum: string;
  rechnung_bemerkung: string;
  rechnung_zahlungsziel: string;
  rechnung_fehlteile: string;
  kurtaxe_betrag: number | null;
  anzahlung_betrag: number;
  anzahlung_erhalten: boolean;
  schluesselcode_eg: string | null;
  schluesselcode_og: string | null;
  schluesselcode_dg: string | null;
  schluesselcode_lz: string | null;
  storniert_am: string | null;
  bestaetigung_extern_versandt: boolean;
  bestaetigung_extern_versandt_am: string | null;
  infos_gruppe_extern_versandt: boolean;
  infos_gruppe_extern_versandt_am: string | null;
  konus_link_versandt: boolean;
  konus_link_versandt_am: string | null;
  konus_daten_eingetragen: boolean;
  konus_daten_eingetragen_am: string | null;
  rechnung_versandt_am: string | null;
  bezahlt_am: string | null;
  anzahlung_erhalten_am: string | null;
  gruppenleiter_anrede: string | null;
  gruppenleiter_geburtsdatum: string | null;
  gruppenleiter_vorname: string | null;
  gruppenleiter_nachname: string | null;
  gruppenleiter_adresse_strasse: string | null;
  gruppenleiter_adresse_hausnummer: string | null;
  gruppenleiter_adresse_plz: string | null;
  gruppenleiter_adresse_ort: string | null;
  gruppenleiter_adresse_land: string | null;
  meldescheinnummer: string | null;
  rechnung_rabatt_prozent: number;
  rechnung_rabatt_grund: string;
  rechnungsbetrag_gesamt: number | null;
}>;

export type BookingsFilter = {
  status_filter?: string;
  task_filter?: string;
  einheit_id?: number;
  von?: string;
  bis?: string;
};

export type BookingsCounts = {
  alle: number;
  intern: number;
  storniert: number;
  konflikt: number;
};

/** Zählt Buchungen für die Tab-Badges in einem Call (statt 4 Einzel-Queries). */
export function fetchBookingsCounts(): Promise<BookingsCounts> {
  return apiFetch<BookingsCounts>("/api/bookings/counts");
}

export type ConflictHit = {
  id: number;
  buchungsreferenz: string;
  gastname: string;
  check_in: string;
  check_out: string;
  check_in_time: string | null;
  check_out_time: string | null;
  einheiten_namen: string;
  status: string;
};

/** Prüft Zeitraum + Einheiten auf Überschneidungen (Live-Konflikthinweis). */
export function fetchBookingConflicts(params: {
  einheiten_ids: number[];
  check_in: string;
  check_out: string;
  check_in_time?: string;
  check_out_time?: string;
  eigene_id?: number;
}): Promise<{ conflicts: ConflictHit[] }> {
  const qs = new URLSearchParams({
    einheiten_ids: params.einheiten_ids.join(","),
    check_in: params.check_in,
    check_out: params.check_out,
  });
  if (params.check_in_time) qs.set("check_in_time", params.check_in_time);
  if (params.check_out_time) qs.set("check_out_time", params.check_out_time);
  if (params.eigene_id != null) qs.set("eigene_id", String(params.eigene_id));
  return apiFetch<{ conflicts: ConflictHit[] }>(`/api/bookings/conflicts?${qs}`);
}

export function fetchBookings(filter: BookingsFilter = {}): Promise<Booking[]> {
  const params = new URLSearchParams();
  if (filter.status_filter) params.set("status_filter", filter.status_filter);
  if (filter.task_filter) params.set("task_filter", filter.task_filter);
  if (filter.einheit_id != null) params.set("einheit_id", String(filter.einheit_id));
  if (filter.von) params.set("von", filter.von);
  if (filter.bis) params.set("bis", filter.bis);
  const qs = params.toString();
  return apiFetch<Booking[]>(`/api/bookings${qs ? `?${qs}` : ""}`);
}

export async function downloadBookingsExcel(filter: BookingsFilter = {}): Promise<void> {
  const params = new URLSearchParams();
  if (filter.status_filter) params.set("status_filter", filter.status_filter);
  if (filter.task_filter) params.set("task_filter", filter.task_filter);
  if (filter.von) params.set("von", filter.von);
  if (filter.bis) params.set("bis", filter.bis);
  const qs = params.toString();
  const res = await fetch(`/api/bookings/export/excel${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error(`Export fehlgeschlagen: HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "buchungen.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}
